import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';
import { countryFlag } from '../../lib/country-utils';

/* ── Types ── */
interface StrainOfDay {
  name: string;
  type?: string;
  thc_min?: number;
  thc_max?: number;
  cbd_min?: number;
  cbd_max?: number;
  effects?: string[];
  description?: string;
  image_url?: string;
}

interface NewsItem {
  title: string;
  url: string;
  date?: string;
  source?: string;
}

interface DataStats {
  strains: number;
  hash_types: number;
  countries: number;
  dispensaries: number;
}

/* ── Cannabis Facts ── */
const CANNABIS_FACTS = [
  'Indica strains are named after India, where the plants were first classified by Jean-Baptiste Lamarck in 1785.',
  'Terpenes like myrcene and limonene give strains their distinct aromas — myrcene is earthy, limonene is citrusy.',
  'The most popular strain type worldwide is Hybrid, blending Indica and Sativa genetics.',
  'Purple coloring in buds comes from anthocyanins, pigments activated by cold temperatures during flowering.',
  'Cannabis has over 100 identified cannabinoids beyond THC and CBD, including CBG, CBN, and THCV.',
  'The trichomes on cannabis buds are tiny resin glands that produce cannabinoids and terpenes.',
  'Sativa strains typically grow taller with narrow leaves, while Indica plants are shorter and bushier.',
  'The "entourage effect" theory suggests cannabinoids and terpenes work better together than in isolation.',
  'Cannabis has been cultivated by humans for over 10,000 years, making it one of the oldest crops.',
  'Pinene, found in many strains, is the same terpene that gives pine trees their scent.',
  'Linalool, a terpene in some strains, is also the primary aromatic compound in lavender.',
  'The word "cannabis" comes from the ancient Greek "kannabis," which may derive from Scythian origins.',
  'Autoflowering strains contain Cannabis ruderalis genetics, which flower based on age rather than light cycles.',
  'Kief is the collection of loose trichomes that fall off cannabis buds — it is highly concentrated.',
];

/* ── Skeleton Loader ── */
function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />
  );
}

/* ── Type Badge Color ── */
function typeBadgeColor(type?: string | null | any) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'sativa': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'indica': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'hybrid': return 'bg-green-500/20 text-green-300 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

export default function ExploreTab({ userCountry }: CountryProps) {
  const [strain, setStrain] = useState<StrainOfDay | null>(null);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [stats, setStats] = useState<DataStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [factIndex, setFactIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [strainRes, newsRes, statsRes] = await Promise.allSettled([
          fetch(getApiUrl('/api/strain-of-the-day')),
          fetch(getApiUrl('/api/cannabis-news')),
          fetch(getApiUrl('/api/data-stats')),
        ]);
        if (strainRes.status === 'fulfilled' && strainRes.value.ok) {
          setStrain(await strainRes.value.json());
        }
        if (newsRes.status === 'fulfilled' && newsRes.value.ok) {
          setNews(await newsRes.value.json());
        }
        if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
          const raw = await statsRes.value.json();
          setStats({
            strains: raw.strains_data || 0,
            hash_types: raw.hash_types || 0,
            countries: raw.country_laws || 0,
            dispensaries: raw.dispensaries_worldwide || 0,
          });
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Auto-rotate facts every 8s
  const nextFact = useCallback(() => {
    setFactIndex(i => (i + 1) % CANNABIS_FACTS.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextFact, 8000);
    return () => clearInterval(timer);
  }, [nextFact]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-8"
    >
      {/* Hero Banner */}
      <motion.div
        variants={staggerItem}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-cannabis-600/20 via-purple-600/10 to-cannabis-500/5 border border-cannabis-500/30 p-8 md:p-12"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cannabis-600/10 to-transparent pointer-events-none" />
        <div className="relative space-y-3">
          <p className="text-cannabis-400 text-sm font-bold uppercase tracking-wider">Welcome to the Cannabis Hub</p>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
            ◇ Explore the World of Cannabis
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Discover strains, learn about global laws, find dispensaries, and share your trip reports from 66+ countries
          </p>
        </div>
      </motion.div>
      {/* Strain of the Day Hero */}
      {strain && (
        <motion.div
          variants={staggerItem}
          className="relative overflow-hidden rounded-2xl border border-[#4ade80]/20 bg-gradient-to-br from-[#0a0a0a] to-[#111] p-6 md:p-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#4ade80]/5 to-transparent pointer-events-none" />
          <div className="relative flex flex-col md:flex-row gap-6 items-center">
            {!imageError && strain.image_url ? (
              <img
                src={strain.image_url}
                alt={strain.name}
                className="w-40 h-40 md:w-56 md:h-56 object-cover rounded-xl border border-white/10"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-40 h-40 md:w-56 md:h-56 rounded-xl border border-white/10 bg-gradient-to-br from-[#4ade80]/20 to-green-900/20 flex items-center justify-center">
                <span className="text-6xl">◇</span>
              </div>
            )}
            <div className="flex-1 text-center md:text-left space-y-3">
              <p className="text-[#d4a574] text-sm font-semibold uppercase tracking-wider">
                Strain of the Day
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                {strain.name}
              </h2>
              {strain.type && (
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border ${typeBadgeColor(strain.type)}`}>
                  {strain.type}
                </span>
              )}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start text-sm">
                {(strain.thc_min != null || strain.thc_max != null) && (
                  <span className="text-[#4ade80]">
                    {strain.thc_min ?? '?'}-{strain.thc_max ?? '?'}% THC
                  </span>
                )}
                {(strain.cbd_min != null || strain.cbd_max != null) && (
                  <span className="text-blue-400">
                    {strain.cbd_min ?? '?'}-{strain.cbd_max ?? '?'}% CBD
                  </span>
                )}
              </div>
              {Array.isArray(strain.effects) && strain.effects.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {strain.effects.map((e, i) => (
                    <span key={typeof e === 'string' ? e : `effect-${i}`} className="px-2 py-0.5 text-xs rounded-full bg-[#4ade80]/10 text-[#4ade80] border border-[#4ade80]/20">
                      {typeof e === 'string' ? e : String(e)}
                    </span>
                  ))}
                </div>
              )}
              {strain.description && (
                <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                  {strain.description}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Enhanced Stats Bar */}
      {stats && (
        <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Strains', value: stats.strains, emoji: '◇', color: 'from-green-600 to-green-700' },
            { label: 'Hash Types', emoji: '⬛', value: stats.hash_types, color: 'from-amber-600 to-amber-700' },
            { label: 'Countries', emoji: '◆', value: stats.countries, color: 'from-blue-600 to-blue-700' },
            { label: 'Dispensaries', emoji: '◈', value: stats.dispensaries, color: 'from-purple-600 to-purple-700' },
          ].map(s => (
            <motion.div
              key={s.label}
              whileHover={{ scale: 1.05, y: -5 }}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.color} border border-white/10 p-5 text-center cursor-pointer group`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative space-y-2">
                <div className="text-3xl">{s.emoji}</div>
                <div className="text-2xl md:text-3xl font-black text-white">{(s.value ?? 0).toLocaleString()}</div>
                <div className="text-xs font-semibold text-white/80">{s.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Quick Access Features */}
      <motion.div variants={staggerItem} className="space-y-3">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider text-white/60">Explore More</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { title: '◆ World Map', desc: 'See legality by country', highlight: 'from-blue-600/20' },
            { title: '▶ Travel Tips', desc: 'Cannabis tourism guide', highlight: 'from-green-600/20' },
            { title: '◇ Laws & Rights', desc: 'Legal status worldwide', highlight: 'from-amber-600/20' },
          ].map((item, i) => (
            <motion.div
              key={i}
              variants={staggerItem}
              whileHover={{ scale: 1.02, x: 5 }}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${item.highlight} border border-white/10 p-5 cursor-pointer hover:border-white/30 transition-all`}
            >
              <p className="text-white font-semibold text-lg mb-1">{item.title}</p>
              <p className="text-white/60 text-sm">{item.desc}</p>
              <div className="absolute top-2 right-3 text-2xl opacity-10">→</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Your Country Card - Enhanced */}
      {userCountry && (
        <motion.div
          variants={staggerItem}
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600/20 to-blue-600/10 border border-cyan-500/30 p-6"
        >
          <div className="absolute top-0 right-0 text-5xl opacity-10">◆</div>
          <div className="relative space-y-2">
            <p className="text-cyan-400 text-xs uppercase tracking-wider font-bold">Your Region</p>
            <p className="text-white font-bold text-2xl">{countryFlag(userCountry.code)} {userCountry.name}</p>
            <p className="text-white/60 text-sm">Local cannabis laws & tourism info available</p>
          </div>
        </motion.div>
      )}

      {/* Cannabis Facts Carousel - Enhanced */}
      <motion.div
        variants={staggerItem}
        className="relative overflow-hidden rounded-3xl border border-[#d4a574]/30 bg-gradient-to-br from-[#d4a574]/10 via-[#d4a574]/5 to-transparent p-8"
      >
        <div className="absolute top-4 right-4 text-4xl opacity-20">●</div>
        <div className="relative space-y-4">
          <p className="text-[#d4a574] text-xs font-black uppercase tracking-widest">
            Did You Know?
          </p>
          <AnimatePresence mode="wait">
            <motion.p
              key={factIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="text-white text-base md:text-lg leading-relaxed font-medium min-h-[80px] flex items-center"
            >
              {CANNABIS_FACTS[factIndex]}
            </motion.p>
          </AnimatePresence>
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex gap-1.5">
              {CANNABIS_FACTS.map((_, i) => (
                <motion.button
                  key={i}
                  onClick={() => setFactIndex(i)}
                  whileHover={{ scale: 1.3 }}
                  className={`h-2 rounded-full transition-all ${i === factIndex ? 'bg-[#d4a574] w-8' : 'bg-white/20 w-2'}`}
                />
              ))}
            </div>
            <span className="text-white/40 text-xs font-semibold">{factIndex + 1} / {CANNABIS_FACTS.length}</span>
          </div>
        </div>
      </motion.div>

      {/* Cannabis News Feed - Enhanced */}
      {news.length > 0 && (
        <motion.div variants={staggerItem} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white flex items-center gap-3">
              <span className="text-2xl">■</span> Latest News & Trends
            </h3>
            <span className="text-xs font-semibold text-cannabis-400 bg-cannabis-500/10 px-3 py-1 rounded-full border border-cannabis-500/20">
              {news.length} stories
            </span>
          </div>
          <div className="space-y-2">
            {news.map((item, i) => (
              <motion.a
                key={i}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                variants={staggerItem}
                whileHover={{ scale: 1.02, x: 5 }}
                className="group block p-5 rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.03] to-white/[0.01] hover:from-cannabis-600/10 hover:to-white/[0.02] hover:border-cannabis-500/30 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-white text-sm font-bold group-hover:text-cannabis-300 transition-colors line-clamp-2">{item.title}</p>
                    {item.source && (
                      <p className="text-cannabis-400 text-xs font-semibold">{item.source}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-xl opacity-0 group-hover:opacity-100 transition-opacity">
                    →
                  </div>
                </div>
                {item.date && (
                  <span className="text-white/30 text-xs mt-2 block">{item.date}</span>
                )}
              </motion.a>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
